import { createContext, useContext, useMemo } from 'react';

import { useLegacyDxfViewerToggle } from 'modules/common/viewer/hooks/useLegacyDxfViewerToggle';

type LegacyDxfViewerToggleContextValue = {
  isLegacyDxfViewer: boolean;
  setIsLegacyDxfViewer: (isLegacyDxfViewer: boolean) => void;
};

const LegacyDxfViewerToggleContext =
  createContext<LegacyDxfViewerToggleContextValue | null>(null);
LegacyDxfViewerToggleContext.displayName = 'LegacyDxfViewerToggleContext';

type LegacyDxfViewerToggleProviderProps = {
  children: React.ReactNode;
  partFileUuid: string | null;
};

export const LegacyDxfViewerToggleProvider = ({
  children,
  partFileUuid,
}: LegacyDxfViewerToggleProviderProps) => {
  const { isLegacyDxfViewer, setIsLegacyDxfViewer } =
    useLegacyDxfViewerToggle(partFileUuid);
  const value = useMemo(
    () => ({ isLegacyDxfViewer, setIsLegacyDxfViewer }),
    [isLegacyDxfViewer, setIsLegacyDxfViewer],
  );

  return (
    <LegacyDxfViewerToggleContext.Provider value={value}>
      {children}
    </LegacyDxfViewerToggleContext.Provider>
  );
};
LegacyDxfViewerToggleProvider.displayName = 'LegacyDxfViewerToggleProvider';

export const useLegacyDxfViewerToggleContext =
  (): LegacyDxfViewerToggleContextValue => {
    const context = useContext(LegacyDxfViewerToggleContext);
    if (!context) {
      throw new Error(
        'useLegacyDxfViewerToggleContext must be used within a LegacyDxfViewerToggleProvider',
      );
    }
    return context;
  };
